import React from 'react';
import PropTypes from 'prop-types';
import { createMenuTabReact } from '../../menu-tab/createMenuTab';
import {
    MENU_TAB_FLUID_MODIFIER,
    MENU_TAB_MAX_WIDTH_75,
 } from '../../menu-tab/constants';
import {
    CAROUSEL_TABS_TAB_CSS_CLASS_NAME,
} from './constants';

const propTypes = {
    options: PropTypes.shape({
        /** A Tab can have a label */
        label: PropTypes.string,

        /** A Tab can be selected */
        selected: PropTypes.bool.isRequired,

        /** A Tab can have an icon */
        icon: PropTypes.string,

        /** A Tab icon can have be a text */
        iconLabel: PropTypes.string,

        /** A Tab icon size can be defined */
        iconSize: PropTypes.string,

        /** Take the size of its container */
        fluid: PropTypes.bool,

        /** A Tab can have a counter */
        counter: PropTypes.string,

        /**
         * Style variant:
         *     - 'big-label': The label's text has a bigger size
         *     - 'vertical-icon': Icon and label are vertical aligned
         */
        variant: PropTypes.string,

        /** Callback called when the tab is clicked */
        onClick: PropTypes.func,
    }),

    /** Callback called when the tab is selected */
    onTabSelected: PropTypes.func,

    dateInfo: PropTypes.element,
    gtmAttribute: PropTypes.string,
};

/**
 * A single tab (generally used into a TabbedMenu).
 */
class Tab extends React.Component {
    constructor(props) {
        super(props);
        this.tabDomEl = React.createRef();
        this.state = {
            tab: null,
            selected: props.options.selected,
            counter: props.options.counter,
            dateInfo: props.dateInfo,
            fluid: props.options.fluid,
        };
    }

    /**
     * After the component mounts call the createMenuTabReact to create
     * a tab, then set the state to allow React to re render with the new content.
     *
     * Update the state with the current tab object returned by createMenuTabReact.
     *
     * @return {void}
     */
    componentDidMount() {
        const options = this.props.options;
        const tab = createMenuTabReact(
            this.state.dateInfo || options.label,
            options.icon,
            options.onClick,
            options.iconLabel,
            options.iconSize,
        );

        this.setState({ tab });
    }

    /**
     * When updated props are received, update the state.
     *
     * @param {Object} nextProps The next props
     * @param {Object} prevState The previous state
     * @return {null} || {object} return null or object
     */
    static getDerivedStateFromProps(nextProps, prevState) {
        if (
            nextProps.options.selected === prevState.selected
            && nextProps.options.counter === prevState.counter
        ) {
            return null;
        }

        return {
            selected: nextProps.options.selected,
            counter: nextProps.options.counter,
        };
    }

    /**
     * Check if the tab needs to update.
     *
     * @param {Object} nextProps The next props
     * @param {Object} nextState The next state
     * @return {Boolean} Whether to update or not
     */
    shouldComponentUpdate(nextProps, nextState) {
        return nextState.selected !== this.state.selected ||
            nextState.counter !== this.state.counter ||
            nextState.tab !== this.state.tab;
    }

    /**
     * When the component updates, update the plain js object selected status.
     *
     * @return {void}
     */
    componentDidUpdate() {
        if (this.state.selected) {
            this.state.tab.showAsSelected();
        } else {
            this.state.tab.hideAsSelected();
        }

        this.state.tab.setCount(this.state.counter);
        if (this.state.counter) {
            this.state.tab.showCounter();
        }

        if (!this.state.fluid) {
            this.state.tab.removeModifier(MENU_TAB_FLUID_MODIFIER);
            this.state.tab.addModifier(MENU_TAB_MAX_WIDTH_75);
        }
    }

    /**
     * Renders the tab.
     * Binds an onClick event to call the onTabSelected callback received in
     * the props.
     *
     * @return {React.ReactNode} The react node
     */
    render() {
        let classNames = [ CAROUSEL_TABS_TAB_CSS_CLASS_NAME ];

        if (this.props.options.variant) {
            let variant = this.props.options.variant;
            classNames.push(
                `${CAROUSEL_TABS_TAB_CSS_CLASS_NAME}--${variant}`
            );
        }

        const { tab } = this.state;
        let content = null;
        if (tab) {
            content = tab.el;
        }

        return (
            <span
                className={classNames.join(' ')}
                onClick={this.props.onTabSelected}
                data-spectate-product={this.props.gtmAttribute}
            >
                {content}
            </span>
        );
    }
}

Tab.propTypes = propTypes;

export default Tab;
